import { Space, Typography, Tag } from 'antd';
import { ReactNode, FC } from 'react';
import { Condition, ConditionItem } from '../models';

type ConditionsViewerProps = {
  conditions: Condition;
  layout?: 'vertical' | 'single-line';
};

const operatorLabels: Record<string, string> = {
  '==': 'eşittir',
  '!=': 'eşit değildir',
  '>': 'büyüktür',
  '>=': 'büyük eşittir',
  '<': 'küçüktür',
  '<=': 'küçük eşittir',
  contains: 'içerir',
  startsWith: 'ile başlar',
  endsWith: 'ile biter',
  in: 'içinde',
  notIn: 'içinde değil',
  isNull: 'boş',
  isNotNull: 'boş değil',
  beforeCurrentDate: 'şimdiki tarihten önce',
  beforeEqualCurrentDate: 'şimdiki tarihe eşit veya önce',
  afterCurrentDate: 'şimdiki tarihten sonra',
  afterEqualCurrentDate: 'şimdiki tarihe eşit veya sonra',
  errors: 'hatalar',
  tasks: 'görevler',
  myTasks: 'bana ait görevler',
  activeStatus: 'aktiflik durumu',
  processStatus: 'işlem durumu',
  revisions: 'revizyonlar',
  search: 'arama',
  lastUpdatedAfter: 'sonra güncellenenler',
  lastUpdatedBefore: 'önce güncellenenler',
};

function formatValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Var' : 'Yok';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function renderConditionItem(conditionItem: ConditionItem, layout: 'vertical' | 'single-line'): ReactNode {
  let objectLabel = conditionItem.objectId;
  let operatorLabel = operatorLabels[conditionItem.operator] || conditionItem.operator;
  let value = formatValue(conditionItem.value);
  const includeOldRevisions = conditionItem.includeOldRevisions ? ' (eski revizyonlar dahil)' : '';

  if (conditionItem.objectType === 'record') {
    objectLabel = 'Kayıt';
  }

  if (conditionItem.operator === 'errors' || conditionItem.operator === 'tasks' || conditionItem.operator === 'myTasks') {
    if (conditionItem.value === 'true') {
      value = 'var';
    } else if (conditionItem.value === 'false') {
      value = 'yok';
    } else {
      value = 'tümü';
    }
  }

  if (conditionItem.operator === 'activeStatus') {
    if (conditionItem.value === 'active') {
      value = 'aktif';
    } else if (conditionItem.value === 'inactive') {
      value = 'inaktif';
    } else {
      value = 'tümü';
    }
  }

  if (conditionItem.operator === 'processStatus') {
    if (conditionItem.value === 'processing') {
      value = 'işleniyor';
    } else if (conditionItem.value === 'ready') {
      value = 'hazır';
    } else {
      value = 'tümü';
    }
  }

  if (conditionItem.operator === 'revisions') {
    if (conditionItem.value === 'latest') {
      value = 'son';
    } else if (conditionItem.value === 'previous') {
      value = 'eski';
    } else {
      value = 'tümü';
    }
  }

  return (
    <Space>
      <Typography.Text>{objectLabel}</Typography.Text>
      <Tag style={{ marginRight: 0 }}>{operatorLabel}</Tag>
      {value ? (
        <Typography.Text
          style={
            layout === 'vertical' ? { display: 'inline-block', maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' } : undefined
          }
        >
          {value}
        </Typography.Text>
      ) : null}
      {includeOldRevisions ? <Typography.Text type="secondary">{includeOldRevisions}</Typography.Text> : null}
    </Space>
  );
}

const LOGIC_TAG_WIDTH = 48;

const LogicTag: FC<{ logic: 'AND' | 'OR'; layout: 'vertical' | 'single-line' }> = ({ logic, layout }): JSX.Element => (
  <Tag
    style={{
      borderRadius: '12px',
      backgroundColor: logic === 'AND' ? '#e6f7ff' : '#fff7e6',
      border: `1px solid ${logic === 'AND' ? '#91d5ff' : '#ffd591'}`,
      color: logic === 'AND' ? '#1890ff' : '#fa8c16',
      width: LOGIC_TAG_WIDTH,
      textAlign: 'center',
      display: 'inline-block',
      margin: layout === 'single-line' ? '0 16px' : undefined,
    }}
  >
    {logic === 'AND' ? 'VE' : 'VEYA'}
  </Tag>
);

function renderCondition(condition: Condition, level: number = 0, layout: 'vertical' | 'single-line'): ReactNode {
  const conditionItems = condition.items.map((conditionItem, index) => {
    if ('logic' in conditionItem) {
      const nestedCondition = renderCondition(conditionItem as Condition, level + 1, layout);
      return layout === 'vertical' ? (
        <div key={index} style={{ marginLeft: level * 8, marginBottom: 8, display: 'flex', alignItems: 'center' }}>
          {index > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: 4 }}>
              <LogicTag logic={condition.logic} layout={layout} />
            </div>
          )}
          <div style={{ borderLeft: '1px solid #f0f0f0', flex: 1 }}>{nestedCondition}</div>
        </div>
      ) : (
        <span key={index} style={{ lineHeight: '2rem' }}>
          {index > 0 && <LogicTag logic={condition.logic} layout={layout} />}
          {nestedCondition}
        </span>
      );
    }

    const renderedCondition = renderConditionItem(conditionItem as ConditionItem, layout);
    return layout === 'vertical' ? (
      <div key={index} style={{ marginLeft: level * 8, marginBottom: 8, display: 'flex', alignItems: 'center' }}>
        {index > 0 && <LogicTag logic={condition.logic} layout={layout} />}
        {index === 0 && <span style={{ display: 'inline-block', width: LOGIC_TAG_WIDTH + 8 }} />}
        {renderedCondition}
      </div>
    ) : (
      <span key={index}>
        {index > 0 && <LogicTag logic={condition.logic} layout={layout} />}
        {renderedCondition}
      </span>
    );
  });

  return layout === 'vertical' ? (
    <div style={{ marginBottom: 8 }}>{conditionItems}</div>
  ) : (
    <span>
      {level > 0 && (
        <Tag
          style={{
            backgroundColor: '#f9f0ff',
            border: '1px solid #d3adf7',
            color: '#722ed1',
            padding: '0 4px',
            marginRight: 8,
            borderRadius: '4px',
            fontWeight: 600,
          }}
        >
          (
        </Tag>
      )}
      {conditionItems}
      {level > 0 && (
        <Tag
          style={{
            backgroundColor: '#f9f0ff',
            border: '1px solid #d3adf7',
            color: '#722ed1',
            padding: '0 4px',
            marginLeft: 8,
            borderRadius: '4px',
            fontWeight: 600,
          }}
        >
          )
        </Tag>
      )}
    </span>
  );
}

export const ConditionsViewer: FC<ConditionsViewerProps> = ({ conditions, layout = 'vertical' }): JSX.Element => {
  try {
    return <>{renderCondition(conditions, 0, layout)}</>;
  } catch (error) {
    console.log('Error rendering conditions:', error);
    return <Typography.Text type="danger">Geçersiz koşul formatı</Typography.Text>;
  }
};
